import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Container, Row, Col, Modal, Button } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { FaSearch } from 'react-icons/fa';

import CourseCard from '../components/ui/CourseCard';
import { selectCourses, deleteCourse as deleteCourseAction } from '../redux/coursesSlice';
import { setActiveCourse, setActiveLesson, setPreviewLesson } from '../redux/classroomSlice';
import { deleteCourse } from '../services/supabaseClient/courseService';
import './Dashboard.css';

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const courses = useSelector(selectCourses);
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [courseToDelete, setCourseToDelete] = useState(null);
  const [autoOpenCourseId, setAutoOpenCourseId] = useState(null);

  useEffect(() => {
    const fromCreateCourse = location.state?.openCourseId;
    if (!fromCreateCourse) return;
    setAutoOpenCourseId(fromCreateCourse);
    // Clear one-time route state so refresh/re-render does not reopen modal.
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.pathname, location.state, navigate]);

  // Initialize filtered courses
  useEffect(() => {
    setFilteredCourses(courses);
  }, [courses]);

  // Filter courses based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredCourses(courses);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = courses.filter(
        (course) =>
          course.title?.toLowerCase().includes(query) ||
          course.description?.toLowerCase().includes(query)
      );
      setFilteredCourses(filtered);
    }
  }, [searchQuery, courses]);

  const selectUnitForLaunch = (course, selectedIndex) => {
    const fallbackIndex = course.currentLessonIndex ?? 0;
    const unitIndex =
      typeof selectedIndex === 'number' && selectedIndex >= 0 ? selectedIndex : fallbackIndex;
    const selectedUnit = course.units?.[unitIndex] || course.units?.[fallbackIndex] || null;

    dispatch(setActiveCourse(course));
    dispatch(setActiveLesson(selectedUnit));
    dispatch(setPreviewLesson(null));

    return selectedUnit;
  };

  const handleCourseSelect = (course, selectedIndex) => {
    selectUnitForLaunch(course, selectedIndex);
    navigate('/classroom');
  };

  const handleAnimatedCourseSelect = (course, selectedIndex) => {
    const selectedUnit = selectUnitForLaunch(course, selectedIndex);
    navigate('/lesson-demo', {
      state: {
        courseId: course.id,
        courseTitle: course.title,
        unitId: selectedUnit?.id ?? null,
        unitTitle: selectedUnit?.title ?? null,
        unitObjective: selectedUnit?.objective || selectedUnit?.unit_objective || null,
        presentationMode: 'visual',
      },
    });
  };

  const handleSceneCourseSelect = (course, selectedIndex) => {
    const selectedUnit = selectUnitForLaunch(course, selectedIndex);
    navigate('/lesson-demo/scene', {
      state: {
        courseId: course.id,
        courseTitle: course.title,
        unitId: selectedUnit?.id ?? null,
        unitTitle: selectedUnit?.title ?? null,
        unitObjective: selectedUnit?.objective || selectedUnit?.unit_objective || null,
        presentationMode: 'scene',
      },
    });
  };

  const handleExamCourseSelect = (course, selectedIndex) => {
    const selectedUnit = selectUnitForLaunch(course, selectedIndex);
    navigate('/course-exams', {
      state: {
        courseId: course.id,
        courseTitle: course.title,
        unitId: selectedUnit?.id ?? null,
        unitTitle: selectedUnit?.title ?? null,
        unitObjective: selectedUnit?.objective || selectedUnit?.unit_objective || null,
      },
    });
  };

  const handleDeleteClick = (course) => {
    setCourseToDelete(course);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!courseToDelete) return;

    const { success, error } = await deleteCourse(courseToDelete.id);

    if (success) {
      dispatch(deleteCourseAction(courseToDelete.id));
      setShowDeleteModal(false);
      setCourseToDelete(null);
    } else {
      alert(`Failed to delete course: ${error?.message || 'Unknown error'}`);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setCourseToDelete(null);
  };

  return (
    <Container className="dashboard py-5">
      {/* Search Bar */}
      {courses.length > 0 && (
        <div className="mb-4" style={{ position: 'relative', maxWidth: '500px' }}>
          <input
            type="text"
            className="form-control"
            placeholder="Search your courses..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              paddingRight: '40px',
              borderRadius: '12px',
              border: '1px solid var(--c-border)',
              background: 'var(--c-bg)',
              color: 'var(--c-text)',
            }}
          />
          <FaSearch
            style={{
              position: 'absolute',
              right: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--c-text-subtle)',
              pointerEvents: 'none',
            }}
          />
        </div>
      )}

      {courses.length === 0 ? (
        <div className="text-center py-5">
          <h4>No courses yet</h4>
          <p>Use the create-course icon in the top navbar to get started.</p>
        </div>
      ) : filteredCourses.length === 0 ? (
        <div className="text-center py-5">
          <h4>No courses found</h4>
          <p>Try a different search term</p>
        </div>
      ) : (
        <Row>
          {filteredCourses.map((course) => (
            <Col key={course.id} xs={12} md={6} lg={4} className="mb-4">
              <CourseCard
                course={course}
                onSelect={handleCourseSelect}
                onStartAnimated={handleAnimatedCourseSelect}
                onStartScene={handleSceneCourseSelect}
                onStartExam={handleExamCourseSelect}
                onDelete={handleDeleteClick}
                autoOpen={course.id === autoOpenCourseId}
                onAutoOpened={() => setAutoOpenCourseId(null)}
              />
            </Col>
          ))}
        </Row>
      )}

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={handleCancelDelete} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete Course</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Are you sure you want to delete <strong>{courseToDelete?.title}</strong>?
          </p>
          <p className="text-muted">
            This action cannot be undone. All course data, including units and progress, will be
            permanently deleted.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancelDelete}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleConfirmDelete}>
            Delete Course
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default Dashboard;
